import React from 'react';

// A pledge can hold jewels from several loans. Both OB reports keep one row per pledge, so every
// weight and amount column carries the per-loan split with the pledge total underneath; a pledge
// with a single loan behind it has nothing to split and renders as a plain figure.
export interface LoanBreakdown {
  loanNumber: string;
  grossWeight: number;
  netWeight: number;
  [field: string]: string | number;
}

export const formatWeight = (val: number) => `${(val ?? 0).toFixed(3)} g`;

export const formatCurrency = (val: number) => `₹${(val ?? 0).toLocaleString('en-IN')}`;

export const renderBreakdown = (
  field: string,
  format: (val: number) => string,
  total: number,
  breakdown: LoanBreakdown[] = [],
) => {
  if (breakdown.length < 2) return format(total);

  return (
    <div className="loan-breakdown">
      {breakdown.map((entry) => (
        <div className="loan-breakdown__row" key={entry.loanNumber}>
          <span className="loan-breakdown__label">{entry.loanNumber}:</span>
          <span className="loan-breakdown__value">{format(Number(entry[field] ?? 0))}</span>
        </div>
      ))}
      <div className="loan-breakdown__row loan-breakdown__row--total">
        <span className="loan-breakdown__label">Total:</span>
        <span className="loan-breakdown__value">{format(total)}</span>
      </div>
    </div>
  );
};

// Text columns (closure type, loan type) are per loan too, but a label has no total to add up.
export const renderLabelBreakdown = (
  field: string,
  value: string,
  breakdown: LoanBreakdown[] = [],
) => {
  const orDash = (label: string) => (label.length > 0 ? label : '-');

  if (breakdown.length < 2) return orDash(value ?? '');

  return (
    <div className="loan-breakdown">
      {breakdown.map((entry) => (
        <div className="loan-breakdown__row" key={entry.loanNumber}>
          <span className="loan-breakdown__label">{entry.loanNumber}:</span>
          <span className="loan-breakdown__value">{orDash(String(entry[field] ?? ''))}</span>
        </div>
      ))}
    </div>
  );
};

// The loan numbers arrive joined; stacking them keeps each one level with its breakdown line.
export const renderLoanNumbers = (val: string) => {
  const loanNumbers = (val ?? '').split(',').map((n) => n.trim()).filter(Boolean);

  if (loanNumbers.length === 0) return '-';
  if (loanNumbers.length === 1) return loanNumbers[0];

  return (
    <div className="loan-breakdown">
      {loanNumbers.map((loanNumber) => (
        <div className="loan-breakdown__row" key={loanNumber}>
          {loanNumber}
        </div>
      ))}
    </div>
  );
};
